import { useQuery } from "@tanstack/react-query";
import {
  ArrowUpRight,
  FileSpreadsheet,
  GraduationCap,
  Megaphone,
  Sparkles,
  TriangleAlert,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

/**
 * Le comunicazioni di Datacom in cima alla chat.
 *
 * Pubblicate dall'admin, filtrate per marchio: una promo INIM non deve arrivare
 * a chi ha solo Ajax (richiesta di Umberto del 24/07). Il filtro è lato client
 * perché la lista è corta (max 4) e i marchi dell'utente sono già in memoria.
 */

/**
 * Un colore per categoria (Umberto, 27/08). Non e' decorazione: l'installatore
 * apre l'app in cantiere e deve capire in un colpo d'occhio se quella riga e'
 * un'offerta, un corso o una cosa da sapere subito. Il rosso e' riservato agli
 * avvisi, e proprio per questo non va usato per altro.
 */
const STILI = {
  novita: {
    Icon: Sparkles,
    badge: "bg-emerald-500/15 text-emerald-400",
    accent: "border-emerald-500/40",
    label: "Novità",
  },
  promo: {
    Icon: Megaphone,
    badge: "bg-primary/15 text-primary",
    accent: "border-primary/40",
    label: "Promo",
  },
  corso: {
    Icon: GraduationCap,
    badge: "bg-violet-500/15 text-violet-400",
    accent: "border-violet-500/40",
    label: "Corso",
  },
  listino: {
    Icon: FileSpreadsheet,
    badge: "bg-amber-500/15 text-amber-400",
    accent: "border-amber-500/40",
    label: "Nuovo listino",
  },
  alert: {
    Icon: TriangleAlert,
    badge: "bg-red-500/15 text-red-400",
    accent: "border-red-500/40",
    label: "Avviso",
  },
} as const;

const STILE_DEFAULT = STILI.novita;

export function ComunicazioniHome() {
  const { profile } = useAuth();

  const { data: comunicazioni = [] } = useQuery({
    queryKey: ["comunicazioni"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("comunicazioni")
        .select("id, titolo, testo, categoria, url, brands")
        .eq("attivo", true)
        .order("created_at", { ascending: false })
        .limit(8);
      if (error) throw error;
      return data ?? [];
    },
  });

  const mieiMarchi = profile?.brands ?? [];
  const visibili = comunicazioni
    // brands vuoto = per tutti; altrimenti serve almeno un marchio in comune.
    .filter((c) => !c.brands?.length || c.brands.some((b) => mieiMarchi.includes(b)))
    .slice(0, 4);

  if (visibili.length === 0) return null;

  return (
    <div className="w-full">
      <p className="mb-2 text-center text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        Da Datacom
      </p>
      {/* Su schermo largo le comunicazioni stanno a due a due. Quando sono
          dispari, l'ultima restava incollata a sinistra con mezza riga vuota
          accanto - e con UNA sola comunicazione, che e' il caso normale, era
          tutta la sezione a sembrare storta (Davide, 27/08). Ora l'ultima
          dispari occupa la riga intera: vale per una, per tre, per sempre. */}
      <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
        {visibili.map((c, i) => {
          const ultimaDispari = visibili.length % 2 === 1 && i === visibili.length - 1;
          const stile = STILI[c.categoria as keyof typeof STILI] ?? STILE_DEFAULT;
          const link = /^https?:\/\//i.test(c.url ?? "") ? c.url : null;
          const Contenitore = link ? "a" : "div";

          return (
            <Contenitore
              key={c.id}
              {...(link ? { href: link, target: "_blank", rel: "noreferrer noopener" } : {})}
              className={cn(
                "flex items-start gap-3 rounded-xl border bg-surface/70 p-3.5 text-left transition-all",
                ultimaDispari && "sm:col-span-2",
                stile.accent,
                link && "hover:-translate-y-px hover:bg-surface hover:shadow-glow",
              )}
            >
              <span
                className={cn(
                  "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
                  stile.badge,
                )}
              >
                <stile.Icon className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    "mb-1 inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase",
                    stile.badge,
                  )}
                >
                  {stile.label}
                </span>
                <span className="line-clamp-2 block text-[15px] font-semibold leading-snug text-foreground">
                  {c.titolo}
                </span>
                {c.testo && (
                  <span className="mt-0.5 line-clamp-2 block text-[13px] leading-snug text-muted-foreground">
                    {c.testo}
                  </span>
                )}
              </span>
              {link && (
                <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
              )}
            </Contenitore>
          );
        })}
      </div>
    </div>
  );
}
